/**
 * Google Mobile Auth-style clean authentication popup & route (`/login`).
 *
 * Unified single-input flow:
 * 1. User enters their email (no manual "new vs existing" choice).
 * 2. Backend automatically detects:
 *    - Existing account: prompts Passkey biometric assertion (Touch ID / Face ID / USB key).
 *    - New account: sends a 6-digit email verification code, then prompts Passkey creation.
 *
 * Rendered as a Google-style floating auth card with backdrop blur.
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useAuth } from "../auth/AuthContext";
import { loginDeviceEnvelopes, passkeyLogin, type OtpTicket } from "../api";
import { authDebug, describeEnvelope } from "../auth/debug";
import { diagnosePasskey, installPasskeyDebug } from "../auth/passkeyDebug";
import { TaskJinnLogo } from "../components/TaskJinnLogo";

type AuthStep = "email" | "existing_passkey" | "new_otp";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });

  const [step, setStep] = useState<AuthStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [deviceName] = useState("Primary Device Passkey");
  const [ticket, setTicket] = useState<OtpTicket | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [diagnosis, setDiagnosis] = useState<string | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const destination = search.redirect ?? "/account";

  const handleClose = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      void navigate({ to: "/" });
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // `window.tjPasskeyDebug("you@example.com")` — see ../auth/passkeyDebug.ts.
  useEffect(() => {
    installPasskeyDebug();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return;

    setNotice(null);
    auth.clearError();
    setChecking(true);

    // 1. Check if the account already exists with an authorized Passkey device
    try {
      const login = await passkeyLogin({ email: cleanEmail });
      const envelopes = loginDeviceEnvelopes(login);
      authDebug("login-page:account-lookup", {
        email: cleanEmail,
        workerReturnsDeviceEnvelopes: Array.isArray(login.device_envelopes),
        envelopeCount: envelopes.length,
      });
      envelopes.forEach((envelope, index) => {
        authDebug(`login-page:envelope[${index}]`, describeEnvelope(envelope));
      });
      if (envelopes.length > 0) {
        setStep("existing_passkey");
        setNotice(null);
        setChecking(false);
        return;
      }
    } catch (cause) {
      // 404 / 501 / No account found -> seamlessly route to new account verification
      authDebug("login-page:account-lookup-miss", {
        email: cleanEmail,
        error: cause instanceof Error ? cause.message : String(cause),
      });
    }

    // 2. New account path: request a 6-digit verification code
    try {
      const issued = await auth.requestOtp(cleanEmail);
      setTicket(issued.ticket);
      setPreview(issued.devOtpPreview ?? null);
      setStep("new_otp");
      setNotice(`We sent a 6-digit code to ${cleanEmail}`);
    } catch {
      // auth.error is set by provider
    } finally {
      setChecking(false);
    }
  };

  /**
   * "The browser showed the QR instead of my passkey" has no error to read, so the failure gets a
   * button: it compares what the account owns with what this browser actually holds and prints the
   * verdict on the card (and to the console). Same code path as `window.tjPasskeyDebug(email)`.
   */
  const handleDiagnose = async () => {
    setDiagnosing(true);
    setDiagnosis(null);
    try {
      const result = await diagnosePasskey(email.trim().toLowerCase());
      setDiagnosis(result.verdict);
    } catch (cause) {
      setDiagnosis(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setDiagnosing(false);
    }
  };

  const handlePasskeySignIn = async () => {
    try {
      await auth.signIn(email);
      await navigate({ to: destination });
    } catch (cause) {
      authDebug("login-page:sign-in-failed", {
        error: cause instanceof Error ? cause.message : String(cause),
      });
      // auth.error renders below
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket) return;
    try {
      await auth.completeSignup({
        email: email.trim().toLowerCase(),
        ticket,
        code: code.trim(),
        deviceName,
      });
      await navigate({ to: destination });
    } catch {
      // auth.error renders below
    }
  };

  const handleResendCode = async () => {
    setNotice(null);
    auth.clearError();
    try {
      const issued = await auth.requestOtp(email.trim().toLowerCase());
      setTicket(issued.ticket);
      setPreview(issued.devOtpPreview ?? null);
      setNotice("New verification code sent to your inbox.");
    } catch {
      // auth.error renders below
    }
  };

  const handleSwitchToOtp = async () => {
    setChecking(true);
    auth.clearError();
    try {
      const issued = await auth.requestOtp(email.trim().toLowerCase());
      setTicket(issued.ticket);
      setPreview(issued.devOtpPreview ?? null);
      setStep("new_otp");
      setNotice("Verification code sent to your email.");
    } catch {
      // auth.error renders below
    } finally {
      setChecking(false);
    }
  };

  const handleReset = () => {
    setStep("email");
    setTicket(null);
    setPreview(null);
    setCode("");
    setNotice(null);
    setDiagnosis(null);
    auth.clearError();
  };

  // If already authenticated, show a clean signed-in card
  if (auth.session) {
    return (
      <div
        className="auth-backdrop"
        data-testid="login-page"
        onClick={handleBackdropClick}
      >
        <div className="auth-modal-card text-center">
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="auth-close-btn"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <TaskJinnLogo className="w-12 h-12 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight">You are signed in</h1>
          <p className="mt-2 text-sm text-stone-500">
            {auth.session.email ?? "TensileAuth session"} · {auth.session.role ?? "member"}
          </p>

          <div className="mt-6 flex flex-col gap-2.5">
            <Link to="/dashboard" className="tj-btn tj-btn-primary w-full py-3 text-center justify-center font-semibold">
              Open Dashboard
            </Link>
            <Link to="/account" className="tj-btn tj-btn-secondary w-full py-3 text-center justify-center font-semibold">
              Account &amp; Devices
            </Link>
            <button
              type="button"
              className="text-xs text-rose-600 hover:text-rose-700 font-semibold mt-2 py-1 cursor-pointer"
              onClick={() => {
                auth.signOut();
                handleReset();
              }}
            >
              Sign out of this session
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="auth-backdrop"
      data-testid="login-page"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      onClick={handleBackdropClick}
    >
      <div className="auth-modal-card">
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="auth-close-btn"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Brand Header */}
        <div className="flex flex-col items-center text-center">
          <TaskJinnLogo className="w-11 h-11 mb-2.5 transition-transform hover:scale-105" />
          <h1 id="auth-modal-title" className="text-2xl font-bold text-stone-900 tracking-tight">
            {step === "email" && "Sign in to TaskJinn"}
            {step === "existing_passkey" && "Verify it's you"}
            {step === "new_otp" && "Check your email"}
          </h1>
          <p className="mt-1 text-sm text-stone-500">
            {step === "email" && "Enter your email to continue"}
            {step === "existing_passkey" && "Touch your Passkey or biometric sensor to continue"}
            {step === "new_otp" && "Enter the 6-digit code sent to your inbox"}
          </p>

          {/* Email Chip with 'Change' option */}
          {step !== "email" && (
            <div className="auth-chip">
              <span className="truncate max-w-[200px]">{email}</span>
              <button
                type="button"
                onClick={handleReset}
                className="auth-chip-change"
              >
                Change
              </button>
            </div>
          )}
        </div>

        {/* Feedback Banners */}
        <div aria-live="polite" className="mt-4 space-y-2">
          {auth.error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-800" role="alert">
              {auth.error}
            </div>
          )}
          {notice && (
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-xs font-medium text-orange-900">
              {notice}
            </div>
          )}
          {preview && (
            <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-center justify-between">
              <span>Local development code:</span>
              <code data-testid="otp-preview" className="font-mono font-bold text-sm bg-white px-2 py-0.5 rounded border border-amber-200">
                {preview}
              </code>
            </div>
          )}
        </div>

        {/* ─── STEP 1: EMAIL INPUT ────────────────────────────────────────── */}
        {step === "email" && (
          <form className="mt-6 flex flex-col gap-4" onSubmit={handleEmailSubmit}>
            <div>
              <label htmlFor="login-email-input" className="auth-label">
                Work or personal email
              </label>
              <input
                id="login-email-input"
                type="email"
                name="email"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="auth-input"
              />
            </div>

            <button
              type="submit"
              className="tj-btn tj-btn-primary w-full py-3.5 text-base font-semibold rounded-xl shadow-sm hover:shadow transition-all justify-center mt-1"
              disabled={checking || auth.busy}
              aria-label="Send verification code"
            >
              {checking ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Checking account…
                </span>
              ) : (
                "Continue"
              )}
            </button>
          </form>
        )}

        {/* ─── STEP 2 (EXISTING): PASSKEY AUTHENTICATION ──────────────────── */}
        {step === "existing_passkey" && (
          <div className="mt-6 flex flex-col gap-4">
            <div className="my-3 flex flex-col items-center justify-center">
              <div className="relative w-20 h-20 rounded-full bg-orange-50 border-2 border-orange-200/80 flex items-center justify-center text-3xl shadow-inner">
                <span className="animate-pulse">🛡️</span>
                <div className="absolute inset-0 rounded-full border-2 border-[var(--accent-terracotta)]/30 animate-ping" />
              </div>
              <p className="mt-3 text-xs text-stone-500 font-medium text-center">
                Fast, passwordless authentication using your hardware Passkey.
              </p>
            </div>

            <button
              type="button"
              className="tj-btn tj-btn-primary w-full py-3.5 text-base font-semibold rounded-xl shadow-sm hover:shadow transition-all justify-center"
              onClick={handlePasskeySignIn}
              disabled={auth.busy}
            >
              {auth.busy ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Authenticating…
                </span>
              ) : (
                "Sign in with Passkey"
              )}
            </button>

            <button
              type="button"
              className="text-xs text-stone-500 hover:text-[var(--accent-terracotta)] font-medium text-center mt-1 cursor-pointer"
              onClick={handleSwitchToOtp}
              disabled={checking || auth.busy}
            >
              {checking ? "Sending code…" : "Sign in with email verification code instead"}
            </button>

            <button
              type="button"
              className="text-[11px] text-stone-400 hover:text-stone-600 font-medium text-center cursor-pointer"
              onClick={handleDiagnose}
              disabled={diagnosing || auth.busy}
              data-testid="diagnose-passkey"
            >
              {diagnosing ? "Checking this browser's passkeys…" : "Not seeing your passkey? Check this browser"}
            </button>

            {diagnosis && (
              <div
                className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-medium text-amber-900"
                role="status"
                data-testid="passkey-diagnosis"
              >
                {diagnosis}
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 2 (NEW): 6-DIGIT EMAIL VERIFICATION ──────────────────── */}
        {step === "new_otp" && (
          <form className="mt-6 flex flex-col gap-4" onSubmit={handleVerifyOtp}>
            <div>
              <label htmlFor="login-otp-input" className="auth-label">
                Verification code
              </label>
              <input
                id="login-otp-input"
                type="text"
                name="otp_code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                className="auth-input auth-input-otp"
              />
            </div>

            <button
              type="submit"
              className="tj-btn tj-btn-primary w-full py-3.5 text-base font-semibold rounded-xl shadow-sm hover:shadow transition-all justify-center"
              disabled={auth.busy || code.length < 6}
              aria-label="Verify code & create Passkey"
            >
              {auth.busy ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Setting up Passkey…
                </span>
              ) : (
                "Verify & Create Passkey"
              )}
            </button>

            <div className="flex items-center justify-between text-xs text-stone-500 mt-1">
              <button
                type="button"
                className="hover:text-[var(--accent-terracotta)] font-medium cursor-pointer"
                onClick={handleResendCode}
                disabled={auth.busy}
              >
                Resend code
              </button>
              <button
                type="button"
                className="hover:text-[var(--accent-terracotta)] font-medium cursor-pointer"
                onClick={handleReset}
              >
                Use different email
              </button>
            </div>
          </form>
        )}

        {/* Security Trust Footer */}
        <div className="mt-8 pt-5 border-t border-stone-100 flex items-center justify-center gap-2 text-xs text-stone-400 font-medium">
          <svg className="w-3.5 h-3.5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span>End-to-end Passkey encryption · Passwords never touch servers</span>
        </div>
      </div>
    </div>
  );
}
